// Pure geometry + winner logic for the week calendar. Framework-free and
// exhaustively table-tested (calendar.test.ts) — the React layer only positions
// what this file computes.
//
// The block-splitting mirrors the API resolver (apps/api/src/services/resolver.ts
// `isActiveAt`): a schedule's time window is evaluated in the screen's timezone,
// and a window that crosses midnight (start > end) has its evening portion on the
// masked day and its morning portion on the day AFTER the masked day. So a
// 22:00–02:00 window on Monday renders as Mon 22:00→24:00 and Tue 00:00→02:00.
//
// Winner marking reuses the resolver tie-break via compareForWin — it is NOT
// re-implemented here.

import type { TargetType } from '@seencast/shared';
import { compareForWin, type RankWindow } from '../../lib/schedule-conflicts.ts';

export const MINUTES_PER_DAY = 1440;
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** A schedule reduced to what the calendar needs to place + rank it. */
export interface CalendarSource {
  id: string;
  playlist_id: string;
  playlist_name: string;
  days_mask: number;
  start_time: string | null; // 'HH:MM' or null = start of day
  end_time: string | null; // 'HH:MM' or null = end of day
  priority: number;
  target_type: TargetType;
  created_at: number;
}

/** One schedule occurrence on a single day column, in minutes from midnight. */
export interface CalendarBlock {
  key: string; // unique per rendered block (schedule id + day + segment)
  scheduleId: string;
  playlistId: string;
  playlistName: string;
  priority: number;
  targetType: TargetType;
  createdAt: number;
  day: number; // 0=Mon … 6=Sun
  startMin: number; // 0..1439
  endMin: number; // 1..1440, always > startMin
  continuesFromPrevDay: boolean; // morning tail of an overnight window
  continuesToNextDay: boolean; // evening portion that spills past midnight
}

export type BlockStatus = 'win' | 'partial' | 'lose';

/** A block plus its resolved winner state and column-lane placement. */
export interface PlacedBlock extends CalendarBlock {
  status: BlockStatus;
  /** Sub-ranges (minutes) of this block that a higher-ranked block overrides. */
  overriddenRanges: Array<[number, number]>;
  lane: number; // 0-based lane within its overlap cluster
  laneCount: number; // total lanes in that cluster
}

/** Parse 'HH:MM' to minutes; null falls back (start→0, end→1440). */
export function timeToMinutes(time: string | null, fallback: number): number {
  if (!time) return fallback;
  const [h, m] = time.split(':').map((n) => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}

/** Format minutes-from-midnight back to 'HH:MM' (24:00 clamps to display value). */
export function minutesToTime(min: number): string {
  const clamped = Math.max(0, Math.min(MINUTES_PER_DAY, Math.round(min)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Expand one schedule into its per-day-column blocks for the week grid, matching
 * the resolver's overnight semantics. All-day windows (null/null) become a single
 * 0→1440 block on each masked day.
 */
export function expandToBlocks(s: CalendarSource): CalendarBlock[] {
  const startMin = timeToMinutes(s.start_time, 0);
  const endMin = timeToMinutes(s.end_time, MINUTES_PER_DAY);
  const out: CalendarBlock[] = [];
  const push = (
    day: number,
    a: number,
    b: number,
    fromPrev: boolean,
    toNext: boolean,
    seg: string,
  ) => {
    if (b <= a) return; // zero-length / degenerate window
    out.push({
      key: `${s.id}:${day}:${seg}`,
      scheduleId: s.id,
      playlistId: s.playlist_id,
      playlistName: s.playlist_name,
      priority: s.priority,
      targetType: s.target_type,
      createdAt: s.created_at,
      day,
      startMin: a,
      endMin: b,
      continuesFromPrevDay: fromPrev,
      continuesToNextDay: toNext,
    });
  };

  for (let d = 0; d < 7; d++) {
    if (!(s.days_mask & (1 << d))) continue;
    if (startMin <= endMin) {
      push(d, startMin, endMin, false, false, 'd');
    } else {
      // Overnight: evening on the masked day, morning tail on the next day column.
      push(d, startMin, MINUTES_PER_DAY, false, true, 'eve');
      push((d + 1) % 7, 0, endMin, true, false, 'morn');
    }
  }
  return out;
}

const rankOf = (b: CalendarBlock): RankWindow => ({
  priority: b.priority,
  target_type: b.targetType,
  created_at: b.createdAt,
});

/** Merge sorted, possibly-adjacent [start,end] ranges. */
function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  if (ranges.length === 0) return [];
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [sorted[0]!.slice() as [number, number]];
  for (let i = 1; i < sorted.length; i++) {
    const last = out[out.length - 1]!;
    const cur = sorted[i]!;
    if (cur[0] <= last[1]) last[1] = Math.max(last[1], cur[1]);
    else out.push(cur.slice() as [number, number]);
  }
  return out;
}

/**
 * Resolve a single day column: assign each block a winner status and the exact
 * sub-ranges where a higher-ranked block overrides it, via a sweep over every
 * block edge. Winner at each segment is chosen with the shared resolver tie-break.
 */
export function resolveColumn(blocks: CalendarBlock[]): Map<string, {
  status: BlockStatus;
  overriddenRanges: Array<[number, number]>;
}> {
  const result = new Map<string, { status: BlockStatus; overriddenRanges: Array<[number, number]> }>();
  if (blocks.length === 0) return result;

  const edges = Array.from(
    new Set(blocks.flatMap((b) => [b.startMin, b.endMin])),
  ).sort((a, b) => a - b);

  // For each block, collect the segments it does NOT win (a higher-ranked block covers them).
  const overridden = new Map<string, Array<[number, number]>>();
  const wonAny = new Map<string, boolean>();
  for (const b of blocks) {
    overridden.set(b.key, []);
    wonAny.set(b.key, false);
  }

  for (let i = 0; i < edges.length - 1; i++) {
    const segStart = edges[i]!;
    const segEnd = edges[i + 1]!;
    if (segEnd <= segStart) continue;
    const active = blocks.filter((b) => b.startMin <= segStart && b.endMin >= segEnd);
    if (active.length === 0) continue;
    const winner = active.reduce((best, cur) => (compareForWin(rankOf(cur), rankOf(best)) > 0 ? cur : best));
    wonAny.set(winner.key, true);
    for (const b of active) {
      if (b.key !== winner.key) overridden.get(b.key)!.push([segStart, segEnd]);
    }
  }

  for (const b of blocks) {
    const ranges = mergeRanges(overridden.get(b.key)!);
    const won = wonAny.get(b.key)!;
    const status: BlockStatus = ranges.length === 0 ? 'win' : won ? 'partial' : 'lose';
    result.set(b.key, { status, overriddenRanges: ranges });
  }
  return result;
}

/**
 * Pack overlapping blocks in a day column into side-by-side lanes (greedy) so
 * concurrent schedules are both visible. Non-overlapping blocks reuse lane 0.
 * Returns each block's lane index and the lane count of its overlap cluster.
 */
export function assignLanes(blocks: CalendarBlock[]): Map<string, { lane: number; laneCount: number }> {
  const out = new Map<string, { lane: number; laneCount: number }>();
  const sorted = [...blocks].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  let cluster: CalendarBlock[] = [];
  let clusterEnd = -1;
  const flush = () => {
    if (cluster.length === 0) return;
    // Greedy lane assignment within the cluster.
    const laneEnds: number[] = [];
    const laneOf = new Map<string, number>();
    for (const b of cluster) {
      let lane = laneEnds.findIndex((end) => end <= b.startMin);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(b.endMin);
      } else {
        laneEnds[lane] = b.endMin;
      }
      laneOf.set(b.key, lane);
    }
    const laneCount = laneEnds.length;
    for (const b of cluster) out.set(b.key, { lane: laneOf.get(b.key)!, laneCount });
    cluster = [];
    clusterEnd = -1;
  };

  for (const b of sorted) {
    if (cluster.length > 0 && b.startMin >= clusterEnd) flush();
    cluster.push(b);
    clusterEnd = Math.max(clusterEnd, b.endMin);
  }
  flush();
  return out;
}

/** Full placement for one day column: blocks with status + lane, ready to render. */
export function placeColumn(blocks: CalendarBlock[]): PlacedBlock[] {
  const resolved = resolveColumn(blocks);
  const lanes = assignLanes(blocks);
  return blocks.map((b) => {
    const r = resolved.get(b.key)!;
    const l = lanes.get(b.key)!;
    return { ...b, status: r.status, overriddenRanges: r.overriddenRanges, lane: l.lane, laneCount: l.laneCount };
  });
}

/** The whole week: seven day columns of placed blocks (index 0=Mon … 6=Sun). */
export function buildWeek(sources: CalendarSource[]): PlacedBlock[][] {
  const all = sources.flatMap(expandToBlocks);
  const week: PlacedBlock[][] = [];
  for (let d = 0; d < 7; d++) {
    week.push(placeColumn(all.filter((b) => b.day === d)));
  }
  return week;
}

/**
 * The schedule id the calendar computes as the winner at a given weekday+minute,
 * or null if nothing covers that slot. Used to reconcile the client-side view
 * against the server resolver (GET /v1/schedules/preview) for the current hour.
 */
export function winnerScheduleAt(
  sources: CalendarSource[],
  weekday: number,
  minute: number,
): string | null {
  const active = sources
    .flatMap(expandToBlocks)
    .filter((b) => b.day === weekday && b.startMin <= minute && b.endMin > minute);
  if (active.length === 0) return null;
  return active.reduce((best, cur) => (compareForWin(rankOf(cur), rankOf(best)) > 0 ? cur : best)).scheduleId;
}

// --- Palette --------------------------------------------------------------------
// Deterministic playlist → color so the same playlist reads the same all week.
// Dark-canvas series (Studio v2): a muted jewel-tone sweep anchored in the brand
// teal→indigo→violet family, extended with rose + bronze so 8 playlists stay
// distinguishable. Every hex carries WHITE block text at ≥4.5:1 (the .wc-block
// label color) — verified: contrast 5.0–6.9:1, luminance 0.10–0.16, so the whole
// row reads as one family rather than eight neon chips on the near-black grid.
const PALETTE = [
  '#0f766e', // teal
  '#0e7490', // cyan
  '#2c56b0', // blue
  '#4f46c8', // indigo
  '#7b3fc4', // violet
  '#9b3aa8', // purple
  '#b23a68', // rose
  '#8a6a1f', // bronze
] as const;

export function playlistColor(playlistId: string): string {
  let hash = 0;
  for (let i = 0; i < playlistId.length; i++) hash = (hash * 31 + playlistId.charCodeAt(i)) | 0;
  return PALETTE[Math.abs(hash) % PALETTE.length]!;
}

/** The winner a block yields to over a given range, in plain words, if any. */
export function statusLabel(status: BlockStatus): string {
  return status === 'win' ? 'Plays' : status === 'partial' ? 'Partly overridden' : 'Overridden';
}
